import type { ReactNode } from "react";
import { Logo } from "@/components/brand/Logo";
import { AuditButton, CtaReassurance, FreeAuditButton, WhatsAppButton } from "@/components/ui/Cta";
import { useContent } from "@/data/content";

type Props = {
  title?: ReactNode;
  body?: ReactNode;
  whatsappLabel?: string;
  whatsappMessage?: string;
  location?: string;
  /** The advisory's own page sells the advisory first; everywhere else the free audit leads. */
  lead?: "audit" | "advisory";
};

/** Closing call to action: the free audit first, then the paid advisory or a service keyword on WhatsApp. */
export function FinalCta({ title, body, whatsappLabel, whatsappMessage, location = "final_cta", lead = "audit" }: Props) {
  const { final } = useContent().home;
  return (
    <section
      id="auditoria"
      aria-labelledby="final-cta-heading"
      className="on-navy relative isolate overflow-hidden bg-brand-primary text-white"
    >
      <div aria-hidden="true" className="light-pool-navy absolute inset-0 -z-10 [--pool-x:50%] [--pool-y:100%]" />
      <div aria-hidden="true" className="absolute left-1/2 top-full -z-10 w-[min(70rem,140vw)] -translate-x-1/2 -translate-y-1/2 opacity-[0.12]">
        <Logo layout="mark" alt="" className="spin-slow w-full" />
      </div>

      <div className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 md:py-36">
        <h2
          id="final-cta-heading"
          data-reveal=""
          className="font-display text-[clamp(2.2rem,5vw,4rem)] font-semibold leading-[1.05] tracking-[-0.025em]"
        >
          {title ?? final.title}
        </h2>
        <p data-reveal="" className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-on-navy-muted sm:text-xl">
          {body ?? final.body}
        </p>
        <div data-reveal="" className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          {lead === "advisory" ? (
            <AuditButton location={location} variant="teal" withIcon />
          ) : (
            <FreeAuditButton location={location} variant="teal" />
          )}
          {whatsappLabel || whatsappMessage ? (
            <WhatsAppButton
              location={location}
              variant="outline-light"
              label={whatsappLabel}
              message={whatsappMessage}
            />
          ) : lead === "advisory" ? (
            <FreeAuditButton location={location} variant="outline-light" />
          ) : (
            <AuditButton location={location} variant="outline-light" />
          )}
        </div>
        {lead === "audit" ? <CtaReassurance tone="navy" className="mt-5" /> : null}
      </div>
    </section>
  );
}
